import * as React from "react"
import { Col, Container, Row, Visible } from "react-grid-system"
import styled from "styled-components"
import Card from "../atoms/Card"
import H4 from "../atoms/typography/H4"
import Subtitle from "../atoms/typography/Subtitle"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import { theme } from "../theme"
import { HomeIcon } from "../atoms/icons/HomeIcon"
import { CallIcon } from "../atoms/icons/CallIcon"
import { MailIcon } from "../atoms/icons/MailIcon"
import LargeButton from "../atoms/buttons/LargeButton"
import BodyLarge from "../atoms/typography/BodyLarge"
import H1 from "../atoms/typography/H1"
import SharpSpringFormContact from "../sharpspring/ContactForm"
import { ArrowRight } from "../atoms/icons/ArrowRight"
import H2 from "../atoms/typography/H2"
import Body from "../atoms/typography/Body"
import PrimaryButton from "../atoms/buttons/PrimaryButton"
import { ADDRESS_GOTHENBURG, ADDRESS_KARLSTAD, ADDRESS_USA } from "../../utils/addresses"

const ContentContainer = styled.div`
  padding: ${(props) => props.theme.spacings.extreme};
`

const Image = styled.img`
  width: 100%;
  height: 460px;
  object-fit: cover;
  border-radius: 12px;
`

export const IconTextSection = styled.div`
  display: flex;
`

export const LinkWithoutDecoration = styled.a`
  color: ${(props) => props.theme.colors.greyDark};
  text-decoration: none;
`

const FormImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: cover;
  max-height: 724px;
  border-radius: 12px;
`

const ContactPage: React.FunctionComponent = () => {
  React.useEffect(() => {
    document.title = "Contact"
  }, [])

  return (
    <div>
      <Container>
        <HorizontalSpacer spacing={2} />
        <H1>Contact us</H1>
        <HorizontalSpacer spacing={4} />
        <Card>
          <ContentContainer>
            <HorizontalSpacer spacing={3} />
            <Row>
              <Col md={10}>
                <H1>Get in touch with us</H1>
                <HorizontalSpacer spacing={2} />
                <BodyLarge>
                  Keeping you connected is our priority! <br /> Let's explore how Satcube can help you stay connected
                  anywhere in the world. Demo, questions, feedback or else, contact us through the form below and we'll
                  get back to you.
                </BodyLarge>
              </Col>
            </Row>

            <HorizontalSpacer spacing={4} />

            <Subtitle>Contact form</Subtitle>
            <HorizontalSpacer spacing={2} />
            <div>
              <Row align="start">
                <Col lg={6}>
                  <SharpSpringFormContact />
                </Col>
                <Col lg={6}>
                  <FormImage src="https://images.ctfassets.net/kzqg847wz5jr/7kdl0DWRUbOsIdc9380pQH/d98685298b5de30c5ba77ab4460170c3/Contact.webp" />
                </Col>
              </Row>
            </div>

            <HorizontalSpacer spacing={8} />

            <Row>
              <Col md={10}>
                <Subtitle>Call, email, or visit us</Subtitle>
                <HorizontalSpacer spacing={2} />
                <BodyLarge>
                  Looking for another way to get in touch with us? You can also reach out directly by phone, email or
                  even pay us a visit in our Swedish or North American offices.
                </BodyLarge>
              </Col>
            </Row>
            <HorizontalSpacer spacing={8} />

            <Row>
              <Col md={6}>
                <H4>Head office</H4>
                <HorizontalSpacer spacing={2} />

                <IconTextSection>
                  <HomeIcon />
                  <Spacer spacing={1} />
                  <div>
                    <BodyLarge>{ADDRESS_GOTHENBURG.street}</BodyLarge>
                    <BodyLarge>
                      {ADDRESS_GOTHENBURG.code} {ADDRESS_GOTHENBURG.city}, {ADDRESS_GOTHENBURG.country}
                    </BodyLarge>
                  </div>
                </IconTextSection>

                <HorizontalSpacer spacing={1} />

                <IconTextSection>
                  <CallIcon />
                  <Spacer spacing={1} />
                  <LinkWithoutDecoration href="tel:0046317010500">
                    <BodyLarge>+46 31 701 05 00</BodyLarge>
                  </LinkWithoutDecoration>
                </IconTextSection>

                <HorizontalSpacer spacing={1} />

                <IconTextSection>
                  <MailIcon />
                  <Spacer spacing={1} />
                  <LinkWithoutDecoration href="mailto:sales@satcube.com">
                    <BodyLarge>sales@satcube.com</BodyLarge>
                  </LinkWithoutDecoration>
                </IconTextSection>
              </Col>

              <Col md={6}>
                <Visible xs>
                  <HorizontalSpacer spacing={2} />
                </Visible>
                <H4>Production</H4>
                <HorizontalSpacer spacing={2} />

                <IconTextSection>
                  <HomeIcon />
                  <Spacer spacing={1} />
                  <div>
                    <BodyLarge>{ADDRESS_KARLSTAD.street}</BodyLarge>
                    <BodyLarge>
                      {ADDRESS_KARLSTAD.code} {ADDRESS_KARLSTAD.city}, {ADDRESS_KARLSTAD.country}
                    </BodyLarge>
                  </div>
                </IconTextSection>

                <HorizontalSpacer spacing={1} />

                <IconTextSection>
                  <CallIcon />
                  <Spacer spacing={1} />
                  <LinkWithoutDecoration href="tel:0046317010500">
                    <BodyLarge>+46 31 701 05 00</BodyLarge>
                  </LinkWithoutDecoration>
                </IconTextSection>

                <HorizontalSpacer spacing={1} />

                <IconTextSection>
                  <MailIcon />
                  <Spacer spacing={1} />
                  <LinkWithoutDecoration href="mailto:sales@satcube.com">
                    <BodyLarge>sales@satcube.com</BodyLarge>
                  </LinkWithoutDecoration>
                </IconTextSection>
              </Col>
            </Row>

            <HorizontalSpacer spacing={4} />

            <Row>
              <Col md={6}>
                <H4>USA office</H4>
                <HorizontalSpacer spacing={2} />

                <IconTextSection>
                  <HomeIcon />
                  <Spacer spacing={1} />
                  <div>
                    <BodyLarge>Satcube Inc.</BodyLarge>
                    <BodyLarge>{ADDRESS_USA.street}</BodyLarge>
                    <BodyLarge>
                      {ADDRESS_USA.city} {ADDRESS_USA.code}
                    </BodyLarge>
                    <BodyLarge>{ADDRESS_USA.country}</BodyLarge>
                  </div>
                </IconTextSection>

                <HorizontalSpacer spacing={1} />

                <IconTextSection>
                  <CallIcon />
                  <Spacer spacing={1} />
                  <LinkWithoutDecoration href="tel:0046317010500">
                    <BodyLarge>+46 31 701 05 00</BodyLarge>
                  </LinkWithoutDecoration>
                </IconTextSection>

                <HorizontalSpacer spacing={1} />

                <IconTextSection>
                  <MailIcon />
                  <Spacer spacing={1} />
                  <LinkWithoutDecoration href="mailto:sales@satcube.com">
                    <BodyLarge>sales@satcube.com</BodyLarge>
                  </LinkWithoutDecoration>
                </IconTextSection>
              </Col>
            </Row>

            <HorizontalSpacer spacing={8} />

            <Row justify="between">
              <Col lg={6} md={12}>
                <Subtitle>Work at Satcube</Subtitle>
                <HorizontalSpacer spacing={2} />
                <BodyLarge>
                  We are always looking for outstanding new talent to grow our team. Are you an individual looking for a
                  new challenge in an innovative business or a student looking for a motivating job outside of your
                  studies? If you are a person who is highly motivated, energetic and looking to join a creative team of
                  dedicated sales- and marketeers, designers, developers, and engineers, please have a look at the
                  available positions below or send us an e-mail to tell us more about yourself and why you’re
                  interested in Satcube. We look forward to hearing from you!
                </BodyLarge>
                <HorizontalSpacer spacing={2} />
                <LinkWithoutDecoration href="https://careers.satcube.com" target="__blank">
                  <LargeButton title="Visit our career page" icon={<ArrowRight color={theme.colors.greyDark} />} />
                </LinkWithoutDecoration>
              </Col>

              <Col lg={6} md={12}>
                <Visible xs md>
                  <HorizontalSpacer spacing={2} />
                </Visible>
                <Image
                  src="https://images.ctfassets.net/kzqg847wz5jr/4BMsecp2KY9gpTLOCffZx2/343fe874983d99258d5b9b6e116604ca/workatsatcube.webp"
                  alt="Satcube employee working"
                />
              </Col>
            </Row>

            <HorizontalSpacer spacing={2.75} />

            <H2>Whistleblower function</H2>
            <HorizontalSpacer spacing={2} />
            <Body>
              Satcube values transparency and openness. We believe that it is important to create a safe environment for
              employees, stakeholders, business partners and third parties to speak up about any wrongdoing they may
              witness.
              <br />
              <br />
              Our goal is to ensure that anyone feel comfortable reporting any misconduct they may encounter without
              fear of retaliation. We believe that this is essential for creating a healthy and productive work
              environment. Our commitment to openness and transparency is reflected in our work life and procedures.
              Encouraging people to come forward and have an open dialogue. Satcube is proud to be a part of the
              movement towards greater accountability and transparency in the workplace!
              <br />
              <br />
              Use this link to get to our report line. Please read the instructions as follows in the link.
            </Body>

            <HorizontalSpacer spacing={2} />
            <a href="https://whistleblowing.hu.ma/795b2502-385e-4bd8-a657-962ea97dd8a1" target="__blank">
              <PrimaryButton title="Go to whistleblower function" iconRight={<ArrowRight />} />
            </a>
          </ContentContainer>
        </Card>
      </Container>
      <HorizontalSpacer spacing={4} />
    </div>
  )
}

export default ContactPage
